import { textContentStyle } from "@cva/renderer";
import {
  activeTextEffect,
  contrastColor,
  defaultTextEffect,
  highlightCss,
  isGradient,
  TEXT_EFFECT_CHOICES,
  type TextEffect,
  type TextEffectChoice,
  type TextElement,
} from "@cva/shared";
import type { CSSProperties, ReactNode } from "react";
import { cn } from "../lib/utils";
import { ColorButton } from "./ColorPicker";
import { FillButton } from "./FillPicker";
import { useEditorApi } from "./store";
import { NumberField, Row, Section, Slider } from "./ui";

/**
 * „Effekte“ for text (docs/04-dokumentformat.md): one effect at a time as
 * preset tiles with live previews, its parameters, and curved text. Shadow
 * and outline are the element's own `shadow` / `style.stroke`.
 */

type Recipe = (element: TextElement) => void;

function useUpdateTexts() {
  const store = useEditorApi();
  return (recipe: Recipe, merge?: string) => {
    const { selection } = store.getState();
    store.getState().update(
      (draft) => {
        for (const id of selection) {
          const element = draft.elements[id];
          if (element?.type === "text") recipe(element as TextElement);
        }
      },
      merge ? { merge } : undefined,
    );
  };
}

/** Applies a choice to an element: every choice replaces the previous effect. */
export function applyTextEffect(element: TextElement, choice: TextEffectChoice): void {
  const fs = element.style.fontSize;
  delete element.effect;
  delete element.shadow;
  delete element.style.stroke;
  if (element.brand?.textStroke) delete element.brand.textStroke;
  if (choice === "none") return;
  if (choice === "shadow") {
    element.shadow = {
      x: Math.round(fs * 0.04),
      y: Math.round(fs * 0.06) + 1,
      blur: Math.round(fs * 0.12) + 2,
      color: "#00000059",
    };
  } else if (choice === "outline") {
    element.style.stroke = {
      color: contrastColor(element.style.color),
      width: Math.max(1, Math.round(fs * 0.04 * 10) / 10),
    };
  } else {
    element.effect = defaultTextEffect(choice);
  }
}

const PREVIEW_SIZE = 22;

function previewElement(element: TextElement, choice: TextEffectChoice): TextElement {
  const preview: TextElement = {
    ...element,
    text: "Ag",
    marks: undefined,
    paragraphs: undefined,
    style: {
      ...element.style,
      fontSize: PREVIEW_SIZE,
      color: "#27272a",
      align: "center",
      uppercase: false,
      underline: false,
      strike: false,
      letterSpacing: 0,
      lineHeight: 1.1,
    },
    brand: undefined,
    curve: undefined,
  };
  applyTextEffect(preview, choice);
  return preview;
}

function EffectTile({
  element,
  choice,
  label,
  active,
  onSelect,
}: {
  element: TextElement;
  choice: TextEffectChoice;
  label: string;
  active: boolean;
  onSelect: () => void;
}) {
  const preview = previewElement(element, choice);
  const highlight = highlightCss(preview) as CSSProperties | null;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      title={label}
      className={cn(
        "flex flex-col items-center gap-1 rounded-lg border bg-white px-1 pt-2 pb-1 text-[11px] text-zinc-600 hover:bg-zinc-50",
        active ? "border-brand-500 ring-1 ring-brand-500" : "border-zinc-200",
      )}
    >
      <span className="flex h-8 items-center justify-center overflow-visible">
        <span style={{ ...textContentStyle(preview), minHeight: undefined }}>
          {highlight ? <span style={highlight}>Ag</span> : "Ag"}
        </span>
      </span>
      <span className="truncate">{label}</span>
    </button>
  );
}

function AmountSlider({
  label,
  value,
  min = 0,
  max = 100,
  onChange,
}: {
  label: ReactNode;
  value: number;
  min?: number;
  max?: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs text-zinc-500">
        <span>{label}</span>
        <span className="tabular-nums">{Math.round(value)}</span>
      </div>
      <Slider value={value} min={min} max={max} onChange={onChange} />
    </div>
  );
}

function EffectParameters({ element }: { element: TextElement }) {
  const update = useUpdateTexts();
  const effect = element.effect;
  /** Changes the current effect of every selected text of the same kind. */
  const setEffect = <T extends TextEffect["type"]>(
    type: T,
    recipe: (effect: Extract<TextEffect, { type: T }>) => void,
    merge?: string,
  ) =>
    update((target) => {
      if (target.effect?.type === type) recipe(target.effect as Extract<TextEffect, { type: T }>);
    }, merge);

  if (!effect) {
    const shadow = element.shadow;
    if (shadow) {
      return (
        <>
          <div className="grid grid-cols-3 gap-2">
            <NumberField
              label="X"
              value={shadow.x}
              onChange={(v) =>
                update((target) => {
                  if (target.shadow) target.shadow.x = v;
                }, "shadowX")
              }
            />
            <NumberField
              label="Y"
              value={shadow.y}
              onChange={(v) =>
                update((target) => {
                  if (target.shadow) target.shadow.y = v;
                }, "shadowY")
              }
            />
            <NumberField
              label="Weich"
              value={shadow.blur}
              min={0}
              onChange={(v) =>
                update((target) => {
                  if (target.shadow) target.shadow.blur = Math.max(0, v);
                }, "shadowBlur")
              }
            />
          </div>
          <ColorButton
            value={shadow.color}
            allowAlpha
            label="Schattenfarbe"
            onChange={(color) =>
              update((target) => {
                if (target.shadow) target.shadow.color = color;
              })
            }
          />
        </>
      );
    }
    const stroke = element.style.stroke;
    if (stroke && stroke.width > 0) {
      const fs = element.style.fontSize;
      return (
        <>
          <AmountSlider
            label="Stärke"
            value={Math.min(100, (stroke.width / fs) * 1000)}
            onChange={(v) =>
              update((target) => {
                if (target.style.stroke)
                  target.style.stroke.width =
                    Math.round(target.style.fontSize * (v / 1000) * 10) / 10;
              }, "textStroke")
            }
          />
          <ColorButton
            value={stroke.color}
            label="Konturfarbe"
            onChange={(color) =>
              update((target) => {
                if (target.style.stroke) target.style.stroke.color = color;
                if (target.brand?.textStroke) delete target.brand.textStroke;
              })
            }
          />
        </>
      );
    }
    return null;
  }

  switch (effect.type) {
    case "lift":
    case "neon":
      return (
        <AmountSlider
          label="Intensität"
          value={effect.intensity}
          onChange={(v) =>
            update((target) => {
              if (target.effect?.type === "lift" || target.effect?.type === "neon")
                target.effect.intensity = v;
            }, "effectIntensity")
          }
        />
      );
    case "hollow":
      return (
        <AmountSlider
          label="Stärke"
          value={effect.thickness}
          onChange={(v) =>
            setEffect(
              "hollow",
              (e) => {
                e.thickness = v;
              },
              "effectThickness",
            )
          }
        />
      );
    case "echo":
    case "glitch":
      return (
        <>
          <AmountSlider
            label="Abstand"
            value={effect.offset}
            onChange={(v) =>
              update((target) => {
                if (target.effect?.type === "echo" || target.effect?.type === "glitch")
                  target.effect.offset = v;
              }, "effectOffset")
            }
          />
          <AmountSlider
            label="Richtung"
            value={effect.direction}
            min={-180}
            max={180}
            onChange={(v) =>
              update((target) => {
                if (target.effect?.type === "echo" || target.effect?.type === "glitch")
                  target.effect.direction = v;
              }, "effectDirection")
            }
          />
          {effect.type === "echo" ? (
            <ColorButton
              value={effect.color ?? element.style.color}
              label="Farbe"
              onChange={(color) =>
                setEffect("echo", (e) => {
                  e.color = color;
                })
              }
            />
          ) : (
            <Row>
              <ColorButton
                value={effect.colors[0]}
                label="Farbe 1"
                onChange={(color) =>
                  setEffect("glitch", (e) => {
                    e.colors[0] = color;
                  })
                }
              />
              <ColorButton
                value={effect.colors[1]}
                label="Farbe 2"
                onChange={(color) =>
                  setEffect("glitch", (e) => {
                    e.colors[1] = color;
                  })
                }
              />
            </Row>
          )}
        </>
      );
    case "highlight":
      return (
        <>
          <ColorButton
            value={effect.color}
            label="Hintergrundfarbe"
            onChange={(color) =>
              setEffect("highlight", (e) => {
                e.color = color;
              })
            }
          />
          <AmountSlider
            label="Abstand"
            value={effect.padding}
            onChange={(v) =>
              setEffect(
                "highlight",
                (e) => {
                  e.padding = v;
                },
                "effectPadding",
              )
            }
          />
          <AmountSlider
            label="Rundung"
            value={effect.radius}
            onChange={(v) =>
              setEffect(
                "highlight",
                (e) => {
                  e.radius = v;
                },
                "effectRadius",
              )
            }
          />
          <AmountSlider
            label="Transparenz"
            value={Math.round((1 - effect.opacity) * 100)}
            onChange={(v) =>
              setEffect(
                "highlight",
                (e) => {
                  e.opacity = 1 - v / 100;
                },
                "effectOpacity",
              )
            }
          />
        </>
      );
    case "gradient":
      return (
        <Row>
          <span className="w-20 shrink-0 text-xs text-zinc-500">Verlauf</span>
          <FillButton
            value={effect.fill}
            label="Verlauf"
            onChange={(paint) =>
              setEffect("gradient", (e) => {
                e.fill = isGradient(paint)
                  ? paint
                  : {
                      type: "linear",
                      angle: 90,
                      stops: [
                        { offset: 0, color: paint },
                        { offset: 1, color: paint },
                      ],
                    };
              })
            }
          />
        </Row>
      );
    default:
      return null;
  }
}

export function TextEffectsSection({ elements }: { elements: TextElement[] }) {
  const update = useUpdateTexts();
  const first = elements[0]!;
  const active = activeTextEffect(first);
  const curve = first.curve ?? 0;
  const setCurve = (value: number) =>
    update((target) => {
      const next = Math.max(-100, Math.min(100, Math.round(value)));
      if (next === 0) delete target.curve;
      else target.curve = next;
    }, "textCurve");
  return (
    <Section title="Effekte">
      <div className="grid grid-cols-4 gap-1.5">
        {TEXT_EFFECT_CHOICES.map((choice) => (
          <EffectTile
            key={choice.value}
            element={first}
            choice={choice.value}
            label={choice.label}
            active={active === choice.value}
            onSelect={() => update((target) => applyTextEffect(target, choice.value))}
          />
        ))}
      </div>
      <EffectParameters element={first} />
      <div className="space-y-1 pt-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-zinc-500">Gebogen</span>
          <NumberField
            label="%"
            value={curve}
            min={-100}
            max={100}
            className="w-20"
            onChange={setCurve}
          />
        </div>
        <Slider value={curve} min={-100} max={100} onChange={setCurve} />
        {curve !== 0 && first.effect?.type === "highlight" ? (
          <p className="text-xs text-zinc-500">
            Der Hintergrund wird bei gebogenem Text nicht gezeigt.
          </p>
        ) : null}
      </div>
    </Section>
  );
}
